import React from 'react';
import {
  Box,
  Container,
  Grid,
  Skeleton,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  BackNavigationAppBar,
  OutlinedIcons,
} from '@marageti/z4-lib';
import { ParticipantInfo, TripMatch } from '@marageti/z4-sdk/lib/travel';
import TripSidebar from '../../components/trip-sidebar';
import ZicassoChat from '../../components/zicasso-chat';
import EditParticipantsContainer from './edit-participants';
import ConversationParticipantNames from './conversation-participant-names';

const {
  OutlinedUserAddIcon,
} = OutlinedIcons;

type Props = {
  conversationTitle: string | undefined,
  tripMatchId: string | undefined,
  tripRequestId: string | undefined,
  partnerId: string | undefined,
  assignedTo: string | undefined,
  travelerId: string | undefined,
  participants: ParticipantInfo[],
  tripMatch?: TripMatch,
  isEditParticipantsOpen: boolean,
  onOpenEditParticipants: () => void,
  onCloseEditParticipants: () => void,
  onConfirmEditParticipants: () => void,
};

const ConversationView = ({
  conversationTitle,
  tripMatchId,
  tripRequestId,
  partnerId,
  assignedTo,
  travelerId,
  participants,
  tripMatch,
  isEditParticipantsOpen,
  onOpenEditParticipants,
  onCloseEditParticipants,
  onConfirmEditParticipants,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));
  const isParticipantsLoading = !participants;

  const renderEditParticipantsButton = () => (
    <IconButton onClick={onOpenEditParticipants}>
      <OutlinedUserAddIcon color="primary" sx={{ fontSize: 20 }} />
    </IconButton>
  );

  return (
    <Box
      sx={{
        position: isMobile ? 'fixed' : 'relative',
        inset: isMobile ? 0 : 'auto',
        backgroundColor: isMobile ? 'primary.contrastText' : 'transparent',
        zIndex: isMobile ? 1200 : 'auto',
        height: isMobile ? '100vh' : 'auto',
        px: 0,
      }}
    >
      <Box
        sx={{
          position: isMobile ? 'sticky' : 'relative',
          top: 0,
          zIndex: 1100,
        }}
      >
        <BackNavigationAppBar
          defaultPath={`/trip-matches/${tripMatchId}/messages`}
          options={{
            replace: true,
          }}
          primary={conversationTitle || <Skeleton width={75} />}
          secondary={(
            <ConversationParticipantNames
              participants={participants}
              isLoading={isParticipantsLoading}
              enableTooltip
              tripMatch={tripMatch}
            />
          )}
          endAdornment={renderEditParticipantsButton()}
        />
      </Box>

      <Container
        maxWidth="xl"
        sx={{
          display: 'flex',
          flex: 1,
          pt: isMobile ? 0 : 3.75,
          minHeight: isMobile ? 'calc(100vh - 72px)' : 'auto',
          overflow: 'hidden',
        }}
      >
        <Grid
          container
          spacing={isMobile ? 0 : 6}
          sx={{ flex: 1 }}
        >
          {!isMobile && (
            <Grid
              item
              desktop={4}
              sx={{
                display: { mobile: 'none', desktop: 'block' },
              }}
              spacing={0}
            >
              <Box
                sx={{
                  position: 'sticky',
                  top: 72,
                  pr: 3,
                  height: 'calc(100vh - 72px - 30px)',
                  overflow: 'auto',
                  pb: 2,
                }}
              >
                <TripSidebar
                  tripMatchId={tripMatchId}
                  tripRequestId={tripRequestId}
                />
              </Box>
            </Grid>
          )}

          <Grid
            item
            mobile={12}
            desktop={8}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: isMobile
                ? 'calc(100dvh - 72px)'
                : 'calc(100vh - 54px)',
            }}
            spacing={isMobile ? 0 : 3}
          >
            <Box sx={{
              flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
            }}
            >
              <ZicassoChat />
            </Box>
          </Grid>
        </Grid>
      </Container>

      <EditParticipantsContainer
        open={isEditParticipantsOpen}
        onClose={onCloseEditParticipants}
        onSuccess={onConfirmEditParticipants}
        partnerId={partnerId}
        assignedTo={assignedTo}
        travelerId={travelerId}
        participants={participants}
      />
    </Box>
  );
};

export default ConversationView;
